// Import here Polyfills if needed. Recommended core-js (npm i -D core-js)
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'

import { stringify } from 'qs'
import isNil from 'lodash/isNil'
import reject from 'lodash/reject'
import reduce from 'lodash/reduce'
import isArray from 'lodash/isArray'

let client: AxiosInstance

export function getClient(): AxiosInstance {
  return client
}

type Options = {
  host?: string
  port?: number
  extractToken?: Function
  createClient?: Function
}

function defaultExtractJwtToken() {
  return window.localStorage.getItem('jwt_token')
}

function createAxiosClient(options: Options) {
  const { host = 'http://localhost', port = 80, extractToken = defaultExtractJwtToken } = options
  const client = axios.create({
    baseURL: `${host}:${port}`
  })
  client.interceptors.request.use(function(config: AxiosRequestConfig) {
    config.headers = config.headers || {}

    let token = extractToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  })
  return client
}

export default function createDataProvider(options: Options) {
  const { createClient = createAxiosClient } = options
  client = createClient(options)
  return dataProvider
}

function dataProvider(type: string, resource: string, params: any) {
  switch (type) {
    case 'GET_LIST': {
      return getList(resource, params)
    }
    case 'GET_ONE': {
      return getOne(resource, params)
    }
    case 'CREATE': {
      return create(resource, params)
    }
    case 'UPDATE': {
      return update(resource, params)
    }
    case 'UPDATE_MANY': {
      return updateMany(resource, params)
    }
    case 'DELETE': {
      return del(resource, params)
    }
    case 'DELETE_MANY': {
      return delMany(resource, params)
    }
    case 'GET_MANY': {
      return getMany(resource, params)
    }
    case 'GET_MANY_REFERENCE': {
      return getManyReference(resource, params)
    }
    default: {
      throw new Error(`Bad data provider type: ${type}`)
    }
  }
}

type GetListParams = {
  filter?: any
  pagination?: {
    perPage?: number
    page?: number
  }
  sort?: {
    field?: string
    order?: string
  }
}

type GetListResponse = {
  data: any
  total: number
}

async function getList(resource: string, params: GetListParams): Promise<GetListResponse> {
  const { pagination = {}, sort = {}, filter = {} } = params
  const { page = 1, perPage = 25 } = pagination
  const { field, order } = sort
  const recordsTo = page * perPage
  const recordsFrom = recordsTo - perPage
  const headers = {
    Prefer: 'count=exact',
    Range: `${recordsFrom}-${recordsTo - 1}`
  }
  const query: any = Object.assign({}, formatFilter(filter))
  if (order && field) {
    query.order = `${field}.${order.toLowerCase()}`
  }
  const queryString = stringify(query, { addQueryPrefix: true })
  const response = await client.get(`/${resource}${queryString}`, {
    headers
  })
  return {
    data: response.data.map((row: any, idx: number) => ensureId(row, idx + 1)),
    total: parseInt(response.headers['content-range'].split('/')[1], 10)
  }
}

type GetOneParams = {
  id: string
}

async function getOne(resource: string, params: GetOneParams) {
  const response = await client.get(`${resource}?id=eq.${params.id}`, {
    headers: {
      accept: 'application/vnd.pgrst.object+json'
    }
  })
  return { data: response.data }
}

type CreateParams = {
  data: any
}
type CreateResponse = {
  data: any
}

async function create(resource: string, params: CreateParams): Promise<CreateResponse> {
  const response = await client.post(resource, params.data, {
    headers: {
      Prefer: 'return=representation',
      accept: 'application/vnd.pgrst.object+json'
    }
  })
  return { data: response.data }
}

type UpdateParams = {
  id: string
  data: any
  previousData: any
}

async function update(resource: string, params: UpdateParams) {
  const { id, previousData = {}, data = {} } = params
  const response = await client.patch(
    `${resource}?id=eq.${id}`,
    {
      ...previousData,
      ...data
    },
    {
      headers: {
        Prefer: 'return=representation',
        accept: 'application/vnd.pgrst.object+json'
      }
    }
  )
  return {
    data: response.data
  }
}

type UpdateManyParams = {
  ids: string[]
  data: any
}

async function updateMany(resource: string, params: UpdateManyParams) {
  const { ids, data = {} } = params
  const response = await client.patch(`${resource}?id=in.(${ids.join(',')})`, data, {
    headers: {
      Prefer: 'return=representation'
    }
  })
  return {
    data: response.data
  }
}

type DeleteParams = {
  id: string
}

async function del(resource: string, params: DeleteParams) {
  const response = await client.delete(`${resource}?id=eq.${params.id}`, {
    headers: {
      Prefer: 'return=representation',
      accept: 'application/vnd.pgrst.object+json'
    }
  })
  return {
    data: response.data
  }
}

type DeleteManyParams = {
  ids: string[]
}

async function delMany(resource: string, params: DeleteManyParams) {
  const response = await client.delete(`${resource}?id=in.(${params.ids.join(',')})`, {
    headers: {
      Prefer: 'return=representation'
    }
  })
  return {
    data: response.data
  }
}

type GetManyParams = {
  ids: string[]
}

async function getMany(resource: string, params: GetManyParams) {
  const response = await client.get(`${resource}?id=in.(${params.ids.join(',')})`, {
    headers: {
      Prefer: 'return=representation'
    }
  })
  return {
    data: response.data
  }
}

type GetManyReferenceParams = {
  id: string
  target: string
  filter?: any
  pagination?: {
    perPage?: number
    page?: number
  }
  sort?: {
    field?: string
    order?: string
  }
}

async function getManyReference(resource: string, params: GetManyReferenceParams) {
  const { filter = {}, id, target, ...rest } = params
  return getList(
    resource,
    Object.assign(Object.assign({}, rest), {
      filter: Object.assign(Object.assign({}, filter), { [target]: `eq.${id}` })
    })
  )
}

function ensureId(row: any, defaultId: string | number) {
  if (!row.id) {
    row.id = defaultId
  }
  return row
}

function joinWithoutEmpty(list: string[]) {
  return reject(list, isNil).join(',')
}

function formatFilter(given: any) {
  return reduce(
    given,
    (result: { [field: string]: string }, value: string | string[], key: string) => {
      const filterValue = formatFilterValue(value)
      if (!filterValue) {
        return result
      }

      if (key === 'select') {
        result[key] = filterValue
        return result
      }

      const [field, op = 'eq'] = key.split('@')

      if (op === 'like' || op === 'ilike') {
        result[field] = `${op}.*${filterValue}*`
        return result
      }

      result[field] = `${op}.${filterValue}`
      return result
    },
    {}
  )
}

function formatFilterValue(value: string[] | string) {
  return isArray(value) ? `(${joinWithoutEmpty(value)})` : value
}
